import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { 
    FaArrowRight, FaSearch, FaChevronRight, FaLayerGroup, FaTags
} from 'react-icons/fa';
import loadImage from '../assets/imageLoader';

const AllCategoriesPage = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await apiClient.get('/categories');
                setCategories(response.data || []);
            } catch (err) {
                console.error("Failed to fetch categories", err);
            } finally {
                setLoading(false);
            }
        };
        fetchCategories();
    }, []);

    const renderIcon = (icon, size = 'text-4xl') => {
        if (!icon) return <FaLayerGroup className={size} />;
        if (icon.startsWith('http') || icon.startsWith('/uploads')) {
            return <img src={loadImage(icon)} alt="Icon" className="w-full h-full object-cover" />;
        }
        return <span className={size}>{icon}</span>;
    };

    const mainIndustries = categories.filter(c => !c.parentId && (
        c.Name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        categories.some(sub => sub.parentId === c.id && sub.Name.toLowerCase().includes(searchTerm.toLowerCase()))
    ));

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mb-4"></div>
            <p className="font-bold text-slate-500">Mapping the marketplace...</p>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50 font-sans pb-32">
            
            {/* --- Hero Section --- */}
            <section className="bg-slate-900 text-white pt-24 pb-28 relative overflow-hidden mb-16 shadow-2xl">
                <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-emerald-900/40 via-transparent to-transparent"></div>
                
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
                        <FaTags className="text-emerald-400 text-xs" />
                        <span className="text-emerald-400 font-black tracking-widest uppercase text-[10px]">Service24 Master Directory</span>
                    </div>
                    
                    <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter leading-tight">
                        What can we <br/><span className="text-emerald-400 font-serif italic">Help you with?</span>
                    </h1>
                    
                    <div className="max-w-2xl mx-auto relative group">
                        <FaSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                        <input 
                            type="text" 
                            placeholder="Search for 'Electrician', 'Harvesting', etc..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-800/50 border-2 border-slate-700/50 backdrop-blur-md rounded-[2rem] py-5 pl-14 pr-6 text-white focus:border-emerald-500 outline-none transition-all shadow-xl text-lg placeholder:text-slate-500"
                        />
                    </div>
                </div>
            </section>

            {/* --- Categories Content --- */}
            <main className="container mx-auto px-6 space-y-20">
                {mainIndustries.length > 0 ? (
                    mainIndustries.map((industry) => (
                        <div key={industry.id} className="space-y-8 animate-fade-in-up">
                            {/* Industry Header - Glassmorphic Style */}
                            <div className="flex flex-col md:flex-row items-center md:items-end gap-6 border-b border-slate-200 pb-8 px-4">
                                <div className="w-20 h-20 rounded-[2rem] bg-white shadow-xl shadow-slate-200/50 flex items-center justify-center border border-slate-100 overflow-hidden group hover:rotate-6 transition-transform">
                                    {renderIcon(industry.Icon)}
                                </div>
                                <div className="text-center md:text-left flex-1">
                                    <h2 className="text-3xl font-black text-slate-800 tracking-tight mb-1">{industry.Name}</h2>
                                    <p className="text-slate-500 font-medium">{industry.Description}</p>
                                </div>
                                <div className="hidden lg:block">
                                    <span className="text-[10px] font-black uppercase text-slate-300 tracking-[0.2em]">Industry Section</span>
                                </div>
                            </div>

                            {/* Sub-services Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {categories
                                    .filter(sub => sub.parentId === industry.id)
                                    .filter(sub => sub.Name.toLowerCase().includes(searchTerm.toLowerCase()))
                                    .map((service) => (
                                    <div 
                                        key={service.id} 
                                        onClick={() => navigate(`/category/${service.Name}`)}
                                        className="bg-white p-8 rounded-[2.5rem] border border-transparent shadow-sm hover:shadow-2xl hover:border-emerald-500/20 hover:-translate-y-2 transition-all duration-500 group cursor-pointer flex flex-col items-center text-center relative overflow-hidden"
                                    >
                                        {/* Hover Glow */}
                                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-50 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        
                                        <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center text-4xl mb-6 text-slate-400 group-hover:bg-emerald-600 group-hover:text-white group-hover:rotate-12 transition-all duration-500 shadow-inner overflow-hidden relative z-10">
                                            {renderIcon(service.Icon)}
                                        </div>
                                        
                                        <div className="relative z-10 flex flex-col items-center h-full">
                                            <h3 className="text-xl font-black text-slate-800 mb-2 group-hover:text-emerald-700 transition-colors leading-tight">{service.Name}</h3>
                                            <p className="text-slate-400 text-sm mb-8 line-clamp-2 font-medium leading-relaxed">{service.Description || 'Professional verified experts available.'}</p>
                                            
                                            <div className="mt-auto py-2 px-6 rounded-full bg-slate-50 text-slate-400 text-[10px] font-black uppercase tracking-[0.15em] group-hover:bg-emerald-600 group-hover:text-white transition-all flex items-center gap-2">
                                                Explore Now <FaChevronRight size={8} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="bg-white p-20 rounded-[3rem] shadow-xl text-center border border-slate-100 max-w-2xl mx-auto animate-fade-in">
                        <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8 text-slate-200">
                            <FaSearch size={40} />
                        </div>
                        <h3 className="text-3xl font-black text-slate-800 mb-4">No results found</h3>
                        <p className="text-slate-500 text-lg font-medium leading-relaxed">
                            We couldn't find any services matching "<span className="text-emerald-600 font-bold">{searchTerm}</span>". 
                            Try checking your spelling or use more general keywords.
                        </p>
                        <button 
                            onClick={() => setSearchTerm('')} 
                            className="mt-10 bg-slate-900 text-white px-10 py-4 rounded-2xl font-black hover:bg-black transition-all shadow-lg active:scale-95"
                        >
                            Clear Search
                        </button>
                    </div>
                )}
            </main>

            {/* --- Premium Support CTA --- */}
            <section className="container mx-auto px-6 mt-32">
                <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-[3.5rem] p-10 lg:p-20 flex flex-col lg:flex-row items-center justify-between gap-12 relative overflow-hidden shadow-2xl shadow-emerald-200 group">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:scale-110 transition-transform duration-1000"></div>
                    <div className="relative z-10 text-center lg:text-left max-w-xl">
                        <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tighter leading-tight">Can't find what you're looking for?</h2>
                        <p className="text-emerald-100 text-lg font-medium leading-relaxed opacity-90">
                            Our network is growing every hour. Contact our concierge team and we'll personally source the right expert for your specific requirement.
                        </p>
                    </div>
                    <div className="relative z-10 flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                        <button className="bg-white text-emerald-700 px-10 py-5 rounded-2xl font-black hover:bg-slate-50 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3 text-lg">
                            Request Custom Service <FaArrowRight />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AllCategoriesPage;
